/**
 * Auth Service
 *
 * Provides authentication token management for use outside of React components.
 * Uses a cached token pattern that gets updated from React components via hooks.
 */

// Cached auth token (set by React components via setAuthToken)
let cachedToken: string | null = null;
let cachedUserId: string | null = null;

/**
 * Set the current auth token
 * Should be called from a React component that has access to InstantDB's useAuth hook
 */
export function setAuthToken(token: string | null): void {
  cachedToken = token;
}

/**
 * Set the current user ID
 * Should be called from a React component that has access to InstantDB's useAuth hook
 */
export function setUserId(userId: string | null): void {
  cachedUserId = userId;
}

/**
 * Get the current auth token for API requests
 * Returns null if not authenticated or token not yet set
 */
export async function getAuthToken(): Promise<string | null> {
  return cachedToken;
}

/**
 * Check if user is currently authenticated (token is set)
 */
export function isAuthenticated(): boolean {
  return !!cachedToken;
}

/**
 * Get the current user ID
 */
export function getCurrentUserId(): string | null {
  return cachedUserId;
}

/**
 * Clear all cached auth data (call on logout)
 */
export function clearAuthCache(): void {
  cachedToken = null;
  cachedUserId = null;
}
